import type { Backend } from "../api/backend";
import type { RadioRunEvent, Segment } from "../api/types";

export interface RunFollowerHandlers {
  /** Segments to upsert, after dropping everything from `truncateFrom` when it is set. */
  onSegments(segments: Segment[], truncateFrom: number | null): void;
  /** The run's status as the server sees it: `on_air`, `paused`, `stopped`. */
  onStatus(status: string): void;
  /** Where the playhead is: the server position of the current block. */
  cursor(): { position: number; offsetSeconds: number } | null;
}

export interface FollowerTiming {
  pollMs: number;
  heartbeatMs: number;
  maxBackoffMs: number;
}

const DEFAULT_TIMING: FollowerTiming = { pollMs: 2_000, heartbeatMs: 20_000, maxBackoffMs: 15_000 };

/**
 * Follows a station the server keeps on air, like the phone's `RadioRunFollower`: reads
 * the station's event log, tells the server where the playhead is, and carries the
 * listener's corrections up.
 *
 * The phone reads the log over a WebSocket; the browser reads the same rows over plain
 * HTTP every two seconds. The server's own socket polls the table no faster, and a
 * browser WebSocket cannot send the bearer header the socket requires.
 */
export class RunFollower {
  private lastSeq: number;
  private stopped = false;
  private lastReportedPosition: number | null = null;
  private lastReportAt = 0;
  private backoffMs = 0;
  private pollTimer: ReturnType<typeof setTimeout> | null = null;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;

  constructor(
    private readonly backend: Backend,
    readonly playlistID: string,
    afterSeq: number,
    private readonly handlers: RunFollowerHandlers,
    private readonly timing: FollowerTiming = DEFAULT_TIMING,
  ) {
    this.lastSeq = afterSeq;
  }

  start(): void {
    this.schedulePoll(0);
    this.heartbeatTimer = setInterval(() => this.heartbeat(), this.timing.heartbeatMs);
  }

  detach(): void {
    this.stopped = true;
    if (this.pollTimer) clearTimeout(this.pollTimer);
    if (this.heartbeatTimer) clearInterval(this.heartbeatTimer);
  }

  /** The playhead moved: only a new position is worth a request. */
  reportCursor(position: number, offsetSeconds = 0): void {
    if (this.stopped || position === this.lastReportedPosition) return;
    this.send(position, offsetSeconds);
  }

  private heartbeat(): void {
    const cursor = this.handlers.cursor();
    if (!cursor || this.stopped) return;
    const stale = Date.now() - this.lastReportAt >= this.timing.heartbeatMs;
    if (cursor.position !== this.lastReportedPosition || stale) {
      this.send(cursor.position, cursor.offsetSeconds);
    }
  }

  private send(position: number, offsetSeconds: number): void {
    this.lastReportedPosition = position;
    this.lastReportAt = Date.now();
    void this.backend.reportCursor(this.playlistID, position, offsetSeconds).catch(() => {
      // The heartbeat repeats it.
      this.lastReportAt = 0;
    });
  }

  private schedulePoll(ms: number): void {
    if (this.stopped) return;
    this.pollTimer = setTimeout(() => void this.poll(), ms);
  }

  private async poll(): Promise<void> {
    try {
      const page = await this.backend.runEvents(this.playlistID, this.lastSeq);
      for (const event of page.events) {
        if (this.stopped) return;
        this.apply(event);
      }
      this.backoffMs = 0;
      // A full page means more is waiting: read on at once.
      this.schedulePoll(page.events.length >= 200 ? 0 : this.timing.pollMs);
    } catch {
      this.backoffMs = Math.min(
        this.timing.maxBackoffMs,
        Math.max(this.timing.pollMs, this.backoffMs * 2),
      );
      this.schedulePoll(this.backoffMs);
    }
  }

  private apply(event: RadioRunEvent): void {
    this.lastSeq = Math.max(this.lastSeq, event.seq);
    if (event.type === "segments") {
      const segments = event.segments ?? [];
      const truncateFrom = event.truncate_from ?? null;
      if (segments.length > 0 || truncateFrom !== null) {
        this.handlers.onSegments(segments, truncateFrom);
      }
    } else if (event.type === "status" && event.kind === "run" && event.status) {
      this.handlers.onStatus(event.status);
    }
  }
}
